import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ResponseModel } from '../../models/response.model';
import { appEnv } from '../../../app.config';

@Injectable({
  providedIn: 'root'
})
export class ShiftService {

  constructor(private http: HttpClient) { }

  getTableData (filters: {start_date: string, end_date: string} = { start_date: '', end_date: ''}): Observable<ResponseModel> {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/table-data?start_date=${filters.start_date ?? ''}&end_date=${filters.end_date ?? ''}`);
  }

  bulkUpdateStatus (ids: number[] = [], statusId: number = 0): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/bulk-update-status`, {ids: ids, status_id: statusId});
  }

  bulkDelete (ids: number[] = []): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/bulk-delete`, {ids: ids});
  }

  reverseBulkAuthorisation (data: any = []): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/reverse-bulk-authorisation`, data);
  }

  reversePublish (data: any = []): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/reverse-bulk-publish`, data);
  }

  updateShiftServiceType (id: number = 0, serviceId: number = 0): Observable<ResponseModel> {
    return this.http.patch<ResponseModel>(`${appEnv.api}shift/update-service-type?id=${id}`, {service_type_id: serviceId});
  }

  assignContractor (shiftId: number = 0, driverId: number = 0, vehicleId: number = 0, roleId: number = 0, isPublished: number = 1, caller: string = '', shipmentId: number = 0) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/assign-contractor`, {shift_id: shiftId, driver_id: driverId, role_id: roleId, vehicle_type_id: vehicleId, published: isPublished, caller: caller, shipment_id: shipmentId});
  }

  removeContractor (assigneeShiftContractorId: number = 0, consignmentId: number = 0) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/remove-contractor`, {
      id: assigneeShiftContractorId,
      shipment_id: consignmentId
    });
  }

  createPublishedDefaultRoleVehicle (data: any = [], shiftIds: number[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/create-published-default-role-vehicle?shift-ids=${shiftIds}`, data);
  }

  update (shiftId: number = 0, data: any = []) {
    return this.http.put<ResponseModel>(`${appEnv.api}shift/update?id=${shiftId}`, data);
  }

  updateContractorTimeLogs (id: number = 0, data: any = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/update-contractor-time-logs?id=${id}`, data);
  }

  updateContractorBreakTimeLogs (id: number = 0, data: any = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/update-contractor-break-logs?id=${id}`, data);
  }

  getShiftSummary (shiftId: number = 0, driverId: number = 0) {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/summary/shift-driver-id?shift_id=${shiftId}&driver_id=${driverId}`);
  }

  getShiftCooldown (shiftId: number = 0, driverId: number = 0) {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/cooldown/shift-driver-id?shift_id=${shiftId}&driver_id=${driverId}`);
  }

  updatePaidType (assigneeId: number = 0, id: number = 0) {
    return this.http.patch<ResponseModel>(`${appEnv.api}shift/assigned-contractor/paid-type`, {id: assigneeId, paid_type_id: id});
  }

  /** Changes the charge and/or pay rate scope independently — pass only the side(s) being changed.
   * charge_item_zone/pay_item_zone (1-8) are only meaningful when the corresponding rate scope is
   * "Item Rate", but are accepted the same way the rate scope ids themselves are. */
  updateRateScope (assigneeId: number = 0, data: {charge_paid_type_id?: number, pay_paid_type_id?: number, charge_item_zone?: number, pay_item_zone?: number} = {}) {
    return this.http.patch<ResponseModel>(`${appEnv.api}shift/assigned-contractor/rate-scope`, {id: assigneeId, ...data});
  }

  /** reload = true only for the Price Breakdown's Reload Updated Rates button: lets the backend replace an
   * already-calculated rate (unless it was edited by hand). Every other caller keeps the existing rate. */
  reCalculateRates(assigneeId: number = 0, reload: boolean = false) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/calculate-rates`, {shift_assigned_contractor_id: assigneeId, ...(reload ? {reload: true} : {})});
  }

  updateRateAdjustmentAmount(assigneeId: number = 0, data: any) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/adjust-rate-amount`, {shift_assigned_contractor_id: assigneeId, ...data});
  }

  /** Live "auto-calculate" preview — recalculates the line items from an edited raw rate field, without persisting anything. */
  previewRateBreakdown (shiftAssigneeId: number = 0, side: 'charge' | 'pay' = 'charge', rate: any = {}) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/preview-rate-breakdown`, {shift_assigned_contractor_id: shiftAssigneeId, side, rate});
  }

  /** The 8 Item Rate Zone dollar values (charge + pay) for whichever rate card is currently matched to this shift assignee — populates the Price Breakdown modal's additive "Item Rate" zone dropdown. */
  getItemRateZoneValues (assigneeId: number = 0) {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/assigned-contractor/${assigneeId}/item-rate-zone-values`);
  }

  /** Full change history for a Price Breakdown rate row (charge and/or pay side), newest first. */
  getPriceBreakdownLogs (thChargeId?: number, thPayId?: number) {
    const params: Record<string, string> = {};
    if (thChargeId) params['th_charge_id'] = String(thChargeId);
    if (thPayId) params['th_pay_id'] = String(thPayId);
    return this.http.get<ResponseModel>(`${appEnv.api}shift/price-breakdown-logs`, { params });
  }

  /** Full change history for a Shift record, newest first. */
  getShiftLogs (shiftId: number) {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/logs`, { params: { shift_id: String(shiftId) } });
  }

  bulkCalculateRates (assigneeIds: number[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/bulk-calculate-rates`, {ids: assigneeIds});
  }

  getAppliedWorkers(shiftId: number = 0): Observable<ResponseModel> {
    return this.http.get<ResponseModel>(`${appEnv.api}shifts/applied-workers/${shiftId}`);
  }

  publishShift (data: any) {
    return this.http.post<ResponseModel>(`${appEnv.api}shifts/publish`, data);
  }

  updateShiftCooldown (post:any) {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/cooldown/update`, post);
  }

  getShiftDetails(shiftUuid: string): Observable<ResponseModel> {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/expand-details/${shiftUuid}`);
  }

  getBulkShiftRates(startDate: string, endDate: string): Observable<ResponseModel> {
    return this.http.get<ResponseModel>(`${appEnv.api}shift/rates?start_date=${startDate}&end_date=${endDate}`);
  }

  updateChargeRateTotal (id: number = 0, totalCharge: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}client-invoice/charge-rate-mod`, {
      id: id,
      charge_rate: {
        total_charge: totalCharge
      }
    });
  }

  updatePayRateTotal (id: number = 0, totalPay: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}driver-payment/pay-rate-mod`, {
      id: id,
      pay_rate: {
        total_pay: totalPay
      }
    });
  }

  updateShiftLockStatus (shiftId: number = 0, lockStatus: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}shift/lock`, {shift_id: shiftId, is_lock: lockStatus});
  }

  updateShiftTailgate (shiftId: number = 0, tailgate: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}shift/tailgate`, {shift_id: shiftId, tailgate: tailgate});
  }

  updateShiftJockey (shiftId: number = 0, jockey: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}shift/jockey`, {shift_id: shiftId, jockey: jockey});
  }

  /** Backend sweep: sets any empty charge/pay rate fields on shifts to 0. Fired once per app load. */
  fixEmptyRates (): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${appEnv.api}shift/fix-empty-rates`, {});
  }

  generateLabels (id: number = 0) {
    return this.http.get<any>(`${appEnv.api}shift/generate-labels?id=${id}`);
  }

}
